import React, { useEffect, useState } from 'react';
import { Accessibility } from 'lucide-react';

const KEY = 'kx_text_zoom';
const LEVELS = [100, 110, 125];

export function applyTextZoom(pct) {
  // body zoom breaks position:fixed below 1024px — desktop gets body zoom,
  // mobile/tablet get a content-scoped zoom class (.kx-zoomable) that never touches fixed elements.
  const v = parseInt(pct, 10) || 100;
  const desktop = window.innerWidth >= 1024;
  document.body.style.zoom = (desktop && v !== 100) ? `${v}%` : '';
  // Sticky headers/navs opt out via .kx-nozoom so menus never wrap under zoom.
  document.documentElement.style.setProperty('--kx-inv-zoom', desktop && v !== 100 ? String(100 / v) : '1');
  document.documentElement.classList.remove('kx-zoom-110', 'kx-zoom-120', 'kx-zoom-125');
  if (!desktop && [110, 120, 125].includes(v)) document.documentElement.classList.add(`kx-zoom-${v}`);
}

if (typeof window !== 'undefined') {
  let kxZoomT;
  window.addEventListener('resize', () => {
    clearTimeout(kxZoomT);
    kxZoomT = setTimeout(() => applyTextZoom(localStorage.getItem(KEY) || '100'), 150);
  });
}

/* Accessibility statement (footer) — the A-A-A control itself lives in the top header. */
export function AccessStatement({ isEs }) {
  return (
    <p className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#B8BDC6]" data-testid="footer-accessibility">
      <Accessibility className="w-4 h-4 text-[#F77F11]" />
      {isEs ? 'Web diseñada pensando en la accesibilidad — ajusta el tamaño del texto arriba (A·A·A).' : 'Website designed with accessibility in mind — adjust text size at the top (A·A·A).'}
    </p>
  );
}

/* Compact A-A-A for light headers — always visible at the top of every view. */
export function TextSizeInline({ className = '' }) {
  const [level, setLevel] = useState(() => {
    const v = parseInt(localStorage.getItem(KEY) || '100', 10);
    return LEVELS.includes(v) ? v : 100;
  });
  useEffect(() => {
    applyTextZoom(level);
    localStorage.setItem(KEY, String(level));
  }, [level]);
  return (
    <div className={`inline-flex items-center gap-0.5 rounded-full border border-neutral-200 bg-neutral-50 p-0.5 ${className}`}
      role="group" aria-label="Text size" data-testid="header-textsize">
      {LEVELS.map((l, i) => (
        <button key={l} type="button" onClick={() => setLevel(l)} aria-pressed={level === l} title={`${l}%`}
          data-testid={`header-textsize-${l}`}
          className={`w-6 h-6 rounded-full font-bold flex items-center justify-center transition-colors ${level === l ? 'bg-[#F77F11] text-white' : 'text-neutral-400 hover:text-neutral-700'}`}
          style={{ fontSize: `${9 + i * 2}px` }}>
          A
        </button>
      ))}
    </div>
  );
}

/* "A A A" visible text-size control + accessibility statement (footer). */
export default function TextSizeControl({ isEs }) {
  const [level, setLevel] = useState(() => {
    const v = parseInt(localStorage.getItem(KEY) || '100', 10);
    return LEVELS.includes(v) ? v : 100;
  });

  useEffect(() => {
    applyTextZoom(level);
    localStorage.setItem(KEY, String(level));
  }, [level]);

  return (
    <div className="flex flex-col items-center lg:items-start gap-2" data-testid="footer-accessibility">
      <p className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#B8BDC6]">
        <Accessibility className="w-4 h-4 text-[#F77F11]" />
        {isEs ? 'Web diseñada pensando en la accesibilidad.' : 'Website Designed with Accessibility in Mind.'}
      </p>
      <div className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/[0.04] p-1"
        role="group" aria-label={isEs ? 'Tamaño del texto' : 'Text size'}>
        <span className="pl-2 pr-1 text-[10.5px] uppercase tracking-wider font-bold text-[#7D848E]">
          {isEs ? 'Texto' : 'Text'}
        </span>
        {LEVELS.map((l, i) => (
          <button
            key={l}
            type="button"
            onClick={() => setLevel(l)}
            aria-pressed={level === l}
            title={`${l}%`}
            className={`rounded-full font-bold transition-colors px-2.5 h-7 ${
              level === l ? 'bg-[#F77F11] text-white' : 'text-[#B8BDC6] hover:text-white'
            }`}
            style={{ fontSize: `${11 + i * 2}px` }}
            data-testid={`text-size-${l}`}
          >
            A
          </button>
        ))}
      </div>
    </div>
  );
}
